import { useEffect, useRef, useState } from "react";
import { Aviso, Cartao, Chip } from "../componentes/ui";
import { mensagemDoConsole } from "../nucleo/console";
import {
  ROTULO_DO_ESTADO_DA_EXPORTACAO,
  emAndamento,
  useMinhasExportacoes,
  type EstadoDaExportacao,
  type Exportacao,
} from "../nucleo/operacao";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";

function quando(valor: string | null): string {
  if (!valor) return "—";
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data);
}

function tom(estado: EstadoDaExportacao): "ok" | "atencao" | "erro" | "neutro" | "aguardando" {
  if (estado === "pronta") return "ok";
  if (estado === "falhou") return "erro";
  if (estado === "expirada") return "neutro";
  return "aguardando";
}

/** As exportações que a própria pessoa pediu: o que está sendo gerado, o que está pronto e o que venceu. */
export function Exportacoes() {
  useTitulo("Exportações");
  const sessao = useSessao();
  const permitido = podeNaSessao(sessao.data, "exportacao:gerar");
  const lista = useMinhasExportacoes(permitido);
  const [recemProntas, setRecemProntas] = useState<Exportacao[]>([]);
  const pendentes = useRef<Set<string>>(new Set());

  /* O aviso na tela: o que estava na fila na leitura anterior e agora está pronto. */
  useEffect(() => {
    if (!lista.data) return;
    const prontas = lista.data.filter(
      (item) => item.estado === "pronta" && pendentes.current.has(item.id),
    );
    if (prontas.length > 0) setRecemProntas((atuais) => [...atuais, ...prontas]);
    pendentes.current = new Set(
      lista.data
        .filter((item) => item.estado === "pendente" || item.estado === "gerando")
        .map((item) => item.id),
    );
  }, [lista.data]);

  if (!sessao.data) return <p className="lead" role="status">Verificando o seu acesso…</p>;
  if (!permitido) {
    return (
      <Aviso tom="marca" data-testid="exportacoes-sem-permissao">
        As exportações são da equipe de atendimento.
      </Aviso>
    );
  }

  return (
    <div data-testid="exportacoes">
      <h1 className="titulo-pagina">Exportações</h1>
      <p className="lead mb-4">
        Arquivos que você pediu para a fila e para os relatórios. Eles são gerados em segundo plano,
        o link vale por 24 horas e só abre para você.
      </p>
      {recemProntas.map((item) => (
        <Aviso tom="sucesso" className="mb-3" key={item.id} data-testid="exportacao-pronta">
          Sua exportação “{item.rotulo}” ficou pronta.
        </Aviso>
      ))}
      {lista.isPending ? (
        <p role="status">Carregando exportações…</p>
      ) : lista.isError ? (
        <Aviso tom="erro">{mensagemDoConsole(lista.error)}</Aviso>
      ) : lista.data.length === 0 ? (
        <Aviso tom="marca">
          Você ainda não pediu nenhuma exportação. Peça pela fila de chamados ou pelos relatórios.
        </Aviso>
      ) : (
        <div className="lista-de-itens">
          {emAndamento(lista.data) ? (
            <p className="nota-rodape" role="status">Gerando… esta lista se atualiza sozinha.</p>
          ) : null}
          {lista.data.map((item) => (
            <Cartao
              titulo={item.rotulo}
              acessorio={<Chip estado={tom(item.estado)}>{ROTULO_DO_ESTADO_DA_EXPORTACAO[item.estado]}</Chip>}
              key={item.id}
              data-testid={`exportacao-${item.id}`}
            >
              <p className="nota-rodape">
                Pedida em {quando(item.criada_em)}
                {item.linhas != null ? ` · ${item.linhas} linha(s)` : ""}
                {item.estado === "pronta" ? ` · vale até ${quando(item.expira_em)}` : ""}
              </p>
              {item.link ? (
                <a className="botao mt-3" href={item.link} download={item.nome_arquivo ?? undefined}>
                  Baixar {item.nome_arquivo ?? "o arquivo"}
                </a>
              ) : null}
              {item.estado === "falhou" ? (
                <Aviso tom="erro" className="mt-3">{item.erro ?? "Não foi possível gerar o arquivo."}</Aviso>
              ) : null}
              {item.estado === "expirada" ? (
                <p className="nota-rodape mt-2">O link venceu. Peça a exportação de novo.</p>
              ) : null}
            </Cartao>
          ))}
        </div>
      )}
    </div>
  );
}
